import { useState } from "react";
import { useSetTileSql, type Tile } from "@/api/queries";
import { ApiError } from "@/api/client";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { SqlBlock } from "@/components/chat/SqlBlock";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { Modal } from "@/components/ui/Modal";
import { AlertIcon, DatabaseIcon, ShieldIcon } from "@/components/icons";
import { tileCsvUrl } from "@/api/queries";

/** A cross-source tile's replay steps, as the two dialogs show them (MS-5). */
function ReplaySteps({ tile, upTo }: { tile: Tile; upTo?: number }) {
  const steps = (tile.replay ?? []).slice(0, upTo);
  return (
    <ol className="flex flex-col gap-2">
      {steps.map((step) => (
        <li key={String(step.query_index)} className="flex flex-col gap-1">
          <span className="type-caption text-ink-disabled">
            q{String(step.query_index)} ·{" "}
            {step.staged ? "join over staged results (DuckDB)" : String(step.connection_name || "a source")}
          </span>
          <SqlBlock sql={String(step.sql ?? "")} />
        </li>
      ))}
    </ol>
  );
}

/**
 * Edit SQL (PRD FR-7.2).
 *
 * The point of this dialog is the order it does things in: **run first, save
 * second**. Preview executes the statement through SQLGuard and the
 * connection's caps and renders the artifact it would produce; Save runs it
 * again and only persists if that succeeded. An administrator therefore cannot
 * store a tile whose query does not work.
 *
 * A refusal is shown as the guard's own reason, the same way chat shows it —
 * a blocked statement is not an application error to paper over, it is the
 * safety property working, and the reason code is what makes it actionable.
 */
export function TileSqlDialog({
  tile,
  open,
  onClose,
}: {
  tile: Tile | null;
  open: boolean;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [preview, setPreview] = useState<Tile | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ code: string; message: string } | null>(null);
  const run = useSetTileSql();

  // Re-seed whenever a different tile opens the dialog.
  const [seeded, setSeeded] = useState<string | null>(null);
  if (open && tile && seeded !== tile.id) {
    setSeeded(tile.id);
    setDraft(tile.sql);
    setPreview(null);
    setNote(null);
    setFailure(null);
  }
  if (!open && seeded !== null) setSeeded(null);

  if (!tile) return null;
  const joins = (tile.replay ?? []).length > 0;

  async function submit(dryRun: boolean) {
    if (!tile) return;
    setFailure(null);
    try {
      const result = await run.mutateAsync({ tileId: tile.id, sql: draft, dryRun });
      setPreview(result.tile);
      setNote(result.note ?? null);
      if (!dryRun) onClose();
    } catch (error) {
      setPreview(null);
      setFailure(
        error instanceof ApiError
          ? { code: error.code, message: error.message }
          : { code: "unknown", message: "The statement could not be run." },
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={tile.query_backed ? "Edit SQL" : "Add SQL"}
      description={
        joins
          ? `This tile joins results from ${tile.connection_name}. Only the join can be edited: one DuckDB SELECT over the staged q tables. Saving re-runs every source query first, so a join that does not work is never stored.`
          : tile.query_backed
            ? "The statement runs before it is saved, so a tile can never hold SQL that does not work."
            : "This tile has no query yet. Give it one and it becomes refreshable."
      }
      footer={
        <>
          <span className="type-caption text-ink-disabled me-auto inline-flex items-center gap-1.5">
            <ShieldIcon className="size-3.5" />
            read-only, guarded and audited
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => void submit(true)} loading={run.isPending}>
            Preview
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit(false)}
            loading={run.isPending}
            disabled={!draft.trim()}
          >
            Run and save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone="neutral" icon={<DatabaseIcon className="size-3" />}>
            {tile.connection_name || "no source"}
          </Chip>
          <Chip tone="neutral">{tile.type.replace(/_/g, " ")}</Chip>
        </div>

        {joins ? (
          <div className="flex flex-col gap-2">
            <p className="type-caption text-ink-disabled">
              Source queries — replayed as they are, each on its own source
            </p>
            <ReplaySteps tile={tile} upTo={-1} />
          </div>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="type-label text-ink">
            {joins ? "Join statement (DuckDB, over the staged results)" : "Statement"}
          </span>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            spellCheck={false}
            rows={8}
            className="bg-surface border-border rounded-element type-code text-ink focus:border-border-emphasized motion-fast w-full resize-y border p-3 outline-none"
            placeholder="SELECT …"
          />
        </label>

        {failure ? (
          <div className="bg-error-subtle rounded-element flex flex-col gap-1 px-3 py-2">
            <p className="text-error type-support flex items-start gap-2">
              <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>{failure.message}</span>
            </p>
            <p className="type-caption text-error">
              {failure.code === "sql_rejected" ? "blocked by SQLGuard" : failure.code} · nothing
              was saved
            </p>
          </div>
        ) : null}

        {note ? (
          <p className="bg-warning-subtle text-warning type-support rounded-element px-3 py-2">
            {note}
          </p>
        ) : null}

        {preview ? (
          <div className="flex flex-col gap-2">
            <p className="type-caption text-ink-disabled">
              Preview · {preview.row_count ?? 0} rows
              {preview.duration_ms != null ? ` · ${preview.duration_ms}ms` : ""}
            </p>
            <ArtifactCard artifact={preview.artifact} csvUrl={tileCsvUrl(tile.id)} />
          </div>
        ) : tile.pipeline.length ? (
          <div className="flex flex-col gap-2">
            <p className="type-caption text-ink-disabled">
              The pipeline this tile came from
            </p>
            {tile.pipeline.map((step, index) => (
              <SqlBlock key={index} sql={String(step.sql ?? "")} />
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/** "View SQL" — the tile's statement and the pipeline it came from (FR-5.4). */
export function TileSqlViewer({
  tile,
  open,
  onClose,
}: {
  tile: Tile | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!tile) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title="SQL behind this tile"
      description={`${tile.connection_name || "No source"}${
        tile.engine ? ` · ${tile.engine}` : ""
      }`}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="flex flex-col gap-4">
        {(tile.replay ?? []).length ? (
          <div className="flex flex-col gap-2">
            <p className="type-caption text-ink-disabled">
              How this tile is computed — every refresh replays these in order
            </p>
            <ReplaySteps tile={tile} />
          </div>
        ) : tile.query_backed ? (
          <div className="flex flex-col gap-1.5">
            <p className="type-caption text-ink-disabled">This tile's query</p>
            <SqlBlock sql={tile.sql} />
          </div>
        ) : (
          <p className="type-support text-ink-secondary">
            This tile is static — the model wrote its values rather than querying them, so there
            is nothing to re-run. Use <strong>Add SQL</strong> to make it refreshable.
          </p>
        )}

        {tile.pipeline.length > 1 || (!tile.query_backed && tile.pipeline.length) ? (
          <div className="flex flex-col gap-2">
            <p className="type-caption text-ink-disabled">
              The full pipeline of the answer it came from
            </p>
            {tile.pipeline.map((step, index) => (
              <SqlBlock key={index} sql={String(step.sql ?? "")} />
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
